import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import Table from "../../../../Components/UiComponents/Table/Table";
import { Editicon, Deleteicon, Viewicon, XCircleblack } from "../../../../Components/IconComponents";
import {
  facilityTableData,
  facilitycolumnsForExport,
} from "../../../../JsonData/CreateFacilityConfig";
import { getData, putDataWithBody } from "../../../../Services/Services";
import {
  getFacilities,
  deleteFacilityURL,
  getThemeURL,
  getFacilityTypeURL,
  getAllContacts,
  getRestrictedRegionListURL,
} from "../../../../Services/EndPoints";
import { Filter as FilterIcon } from "lucide-react";
import Moment from "react-moment";
import { useDispatch } from "react-redux";
import { getExportedColumnsData } from "../../../../Components/UiComponents/utils";
import { setTableData } from "../../../../CentralStore/ExportLMSTable/dataSlice";
import { toast, ToastContainer } from "react-toastify";
import Confirmpopup from "../../Common/ConfirmPopup/Confirmpopup";
import StatusHandler from "../../Common/StatusHandler/StatusHandler";
import Loader from "react-spinner-loader";
import { useTranslation } from "react-i18next";
import { RoleContext } from "../../../..";
import TextEllipsis from "../../../../Components/UiComponents/TextEllipsis/TextEllipsis";
import { Button, Modal } from "react-bootstrap";

const filterLabelStyle = {
  fontSize: "9.5px",
  fontWeight: 800,
  letterSpacing: ".3px",
  textTransform: "uppercase",
  color: "#98A2B3",
};

const filterControlStyle = {
  padding: "6px 10px",
  borderRadius: "8px",
  border: "1px solid #E4E7EC",
  fontSize: "12px",
  background: "#fff",
  color: "#1D2939",
  height: "34px",
  boxSizing: "border-box",
  width: "100%",
};

const norm = (v) => String(v || "").trim().toLowerCase();
const splitNorm = (v) => String(v || "").split(",").map(norm).filter(Boolean);
// Configured values first, then anything only present in the loaded rows; de-duplicated case-insensitively.
const buildOptions = (configured, fromData) => {
  const seen = new Set();
  const out = [];
  [...configured, ...fromData].forEach((raw) => {
    const label = String(raw || "").trim();
    if (label && !seen.has(norm(label))) {
      seen.add(norm(label));
      out.push(label);
    }
  });
  return out.sort((a, b) => a.localeCompare(b));
};

const FacilityList = () => {
  const { roleName } = useContext(RoleContext);
  const { t } = useTranslation();
  const [data, setData] = useState(facilityTableData);
  const dispatch = useDispatch();
  const [loader, setLoader] = useState(false);
  const [searchData, setSearchData] = useState([]);
  const [typeFilter, setTypeFilter] = useState("");
  const [contactFilter, setContactFilter] = useState("");
  const [districtFilter, setDistrictFilter] = useState("");
  const [blockFilter, setBlockFilter] = useState("");
  const [configured, setConfigured] = useState({ types: [], contacts: [], districts: [], blocks: [] });
  const navigate = useNavigate();
  useEffect(() => {
    getFacilityList();
    getFilterOptions();
  }, []);

  useEffect(() => {
    sendDataToCentralStore(searchData);
  }, [searchData]);

  const getFacilityList = async () => {
    setLoader(true);
    const userProfile = await getData(getThemeURL);
    const state_id = userProfile[0]?.state_id;
    var facilityList = await getData(getFacilities.replace("STATEID", state_id));
    let sortedList = facilityList.sort((a, b) => {
      return new Date(b.created_on) - new Date(a.created_on);
    });

    setLoader(false);
    setData(sortedList);
    // sendDataToCentralStore(facilityList);
  };

  // Same Facility Type / Contact / Region lists the Create Facility form uses.
  const getFilterOptions = async () => {
    try {
      const userProfile = await getData(getThemeURL);
      const state_id = userProfile[0]?.state_id;
      const [types, contacts, regions] = await Promise.all([
        getData(getFacilityTypeURL.replace("STATEID", state_id)),
        getData(getAllContacts.replace("STATEID", state_id)),
        getData(getRestrictedRegionListURL.replace("STATEID", state_id)),
      ]);
      const list = (x) => (Array.isArray(x) ? x : []);
      setConfigured({
        types: list(types).map((x) => x.value),
        contacts: list(contacts).map((x) => x.name),
        districts: list(regions).map((x) => x.district),
        blocks: list(regions).map((x) => x.label),
      });
    } catch (e) {
      console.error("Error fetching facility filter options:", e);
    }
  };

  const typeOptions = React.useMemo(
    () => buildOptions(configured.types, data.flatMap((r) => String(r.facility_type || "").split(","))),
    [configured.types, data]
  );
  const contactOptions = React.useMemo(
    () => buildOptions(configured.contacts, data.flatMap((r) => String(r.contact_list || "").split(","))),
    [configured.contacts, data]
  );
  const districtOptions = React.useMemo(
    () => buildOptions(configured.districts, data.map((r) => r.dist_name)),
    [configured.districts, data]
  );
  const blockOptions = React.useMemo(
    () => buildOptions(configured.blocks, data.map((r) => r.block_name)),
    [configured.blocks, data]
  );

  const filteredData = React.useMemo(
    () =>
      (data || []).filter((row) => {
        if (typeFilter && !splitNorm(row.facility_type).includes(norm(typeFilter))) return false;
        if (contactFilter && !splitNorm(row.contact_list).includes(norm(contactFilter))) return false;
        if (districtFilter && norm(row.dist_name) !== norm(districtFilter)) return false;
        if (blockFilter && norm(row.block_name) !== norm(blockFilter)) return false;
        return true;
      }),
    [data, typeFilter, contactFilter, districtFilter, blockFilter]
  );

  const hasActiveFilters = !!(typeFilter || contactFilter || districtFilter || blockFilter);
  const clearAllFilters = () => {
    setTypeFilter("");
    setContactFilter("");
    setDistrictFilter("");
    setBlockFilter("");
  };
  const addNewHandler = () => {
    navigate("/planning/facility-mgmt/create-facility");
    // localStorage.removeItem("DriverRegForm");
  };

  const sendDataToCentralStore = (data) => {
    if (data.length > 0) {
      const columns = facilitycolumnsForExport;
      dispatch(
        setTableData({
          columns,
          tableData: getExportedColumnsData(columns, data),
        })
      );
    }
  };

  const columns = React.useMemo(
    () => [
      {
        Header: t("facilitytableheaders.name"),
        accessor: "name",
        disableSortBy: true,
        Cell: (props) => <TextEllipsis text={props?.value} maxLength={20} />,
      },
      {
        Header: t("facilitytableheaders.address"),
        accessor: "address",
        Cell: (props) => {
          if (props.value) {
            let addressText = props.value;
            if (props.value.length > 15) {
              addressText = props.value.substring(0, 15) + "...";
            }
            return <span title={props.value}>{addressText}</span>;
          } else {
            return "NA";
          }
        },
        disableSortBy: true,
      },
      {
        Header: t("facilitytableheaders.district"),
        accessor: "dist_name",
        disableSortBy: true,
      },
      {
        Header: t("facilitytableheaders.block"),
        accessor: "block_name",
        disableSortBy: true,
      },
      {
        Header: t("facilitytableheaders.description"),
        accessor: "description",
      
        Cell: (props) => {
          const [showModal, setShowModal] = useState(false);
          const [fullDescription, setFullDescription] = useState("");
          const [highlightedDescription, setHighlightedDescription] = useState(""); // New state for highlighted description
      
          const handleShowModal = (description) => {
            setFullDescription(description);
            setHighlightedDescription(description); // Set the description being viewed to highlight...
            setShowModal(true);
          };
      
          const handleCloseModal = () => {
            setShowModal(false);
            setHighlightedDescription(""); // Reset the highlighted description when the modal is closed
          };
      
          const descriptionText = props.value || "NA";
          const truncatedDescription = descriptionText.length > 15 ? `${descriptionText.substring(0, 15)}...` : descriptionText;
      
          // Apply background color if the description is currently being viewed in the modal
          const isHighlighted = descriptionText === highlightedDescription;
          if (showModal) {
            setTimeout(() => {
              let el = document.querySelector('.modal-content');
              el.style.cssText = 'background-color: #1c2038 !important; border-radius: 20px !important;';
            }, 0);
          }
      
          return (
            <span>
              <span
                title={descriptionText} // Tooltip with full description on hover
                style={{
                  cursor: 'pointer',  // Added pointer cursor to indicate clickability
                  backgroundColor: isHighlighted ? '#18A3A0' : 'transparent', // Highlight background when viewed
                  padding: '2px 5px', // Add padding to give a little space around the text
                  borderRadius: '5px', // Rounded corners for the highlighted background
                }}
                onClick={() => handleShowModal(descriptionText)} // Open the modal with full description
              >
                {truncatedDescription}
              </span>
      
              {/* Modal to display the full description */}
              <Modal
                show={showModal}
                onHide={handleCloseModal}
                dialogClassName="custom-modal"
                centered
                style={{
                  borderRadius: '20px',  // Smooth rounded edges
                  border: 'none',        // Remove border to avoid white edges
                  top: '20%',  
                }}
              >
                <Modal.Header
                  style={{
                    backgroundColor: '#2b6591', // Dark blue background for header
                    color: 'white',              // White text in the header
                    borderBottom: 'none',       // Remove bottom border to prevent white edges
                  }}
                >
                  <Modal.Title style={{ color: 'white', marginRight:"250px" }}>Full Description</Modal.Title>
                  <XCircleblack
                    onClick={handleCloseModal}
                    width="1.5em"
                    height="1.5em"
                    className="cursor-pointer"
                  />
                </Modal.Header>
                <Modal.Body
                  style={{
                    backgroundColor: '#fcfcfc', // Blue background for body
                    color: '#001b30',              // White text in the body
                    border: 'none',              // Remove border to prevent white edges
                    borderRadius: '0px',        // Remove radius to make it seamless
                    padding: '20px',             // Added padding for spacing
                    lineHeight: '1.8', 
                  }}
                >
                  <p>{fullDescription}</p>
                </Modal.Body>
                <Modal.Footer
                  style={{
                    backgroundColor: '#fcfcfc', // Darker blue footer
                    color: 'white',              // White text in footer
                    borderTop: 'none',          // Remove top border to prevent white edges
                  }}
                >
                  <Button
                    variant="secondary"
                    onClick={handleCloseModal}
                    style={{
                      backgroundColor: '#2b6591', // Dark blue button
                      borderColor: '#003366',     // Dark border for button
                      borderRadius: '5px',        // Rounded button edges
                    }}
                  >
                    Close
                  </Button>
                </Modal.Footer>
              </Modal>
            </span>
          );
        },
        disableSortBy: true,
      },      
      // {
      //   Header: t("facilitytableheaders.createdon"),
      //   accessor: "created_on",
      //   Cell: (props) => {
      //     if (props.value) {
      //       return <Moment format="DD/MM/YYYY, HH:mm ">{props.value}</Moment>;
      //     } else {
      //       return "NA";
      //     }
      //   },
      //   disableSortBy: true,

      //   // Cell: (props) => <TextEllipsis text={props?.value} maxLength={15} />,
      // },
 
      {
        Header: t("facilitytableheaders.facilitytype"),

        accessor: "facility_type",
        disableSortBy: true,
        Cell: (props) => {
          if (props.value) {
            return <div>{props.value}</div>;
          } else {
            return "NA";
          }
        },
      },
      {
        Header: t("facilitytableheaders.contactlist"),
        accessor: "contact_list",
        disableSortBy: true,
        Cell: (props) => {
          if (props.value) {
            return <TextEllipsis text={props.value} maxLength={20} />;
          } else {
            return "NA";
          }
        },
      },

      {
        Header: t("facilitytableheaders.action"),
        accessor: "actions",
        disableSortBy: true,
        disableFilters: true,
        Cell: (props) => {
          const [isdelete, setIsdelete] = useState(false);
          const [isEditMode, setIsEditMode] = useState(true);
          const isFacility = props.row?.original?.facility_type || ""; // Default to empty string if undefined
 
          const disableDeleteValues = [
            "Community Kitchen",
            "Flood Relief Camp",
            "Animal Shelter",
          ];
 
          // Check if isFacility exists and then apply .includes()
          const isDeleteDisabled =
            isFacility &&
            disableDeleteValues.some((value) => isFacility.includes(value));
 
          const deletePopUp = () => {
            setIsdelete(!isdelete);
          };
 
          const deleteTableDataById = async (id) => {
            let data = { id: id };
            var res = await putDataWithBody(deleteFacilityURL, data, {});
            if (res.status === "success") {
              getFacilityList();
            }
            toast.success(res.msg, {
              position: toast.POSITION.TOP_CENTER,
              autoClose: 2000, // Close after 2 seconds
            });
            setIsdelete(false);
          };
 
          const isMultiSelectDisabled = isEditMode;
 
          const handleEditClick = () => {
            setIsEditMode(false); // Enable edit mode (disable MultiSelectWrapper)
          };
 
          return (
            <div className="usertable__actionsicon">
              <span
                onClick={() =>
                  navigate("/planning/facility-mgmt/view-facility", {
                    state: { data: props.row.original },
                  })
                }
              >
                <Viewicon value={props.value} className="cursor-pointer me-2" />
              </span>

              <span
                className="ms-3"
                onClick={() =>{
                  navigate("/planning/facility-mgmt/create-facility", {
                    state: {
                      data: props.row.original.id,
                      isMultiSelectDisabled: isMultiSelectDisabled,
                      edit: true
                    },
                  });
                  handleEditClick();
                }}>
                <Editicon value={props.value} className="cursor-pointer me-2" />
              </span>
              {roleName === process.env.REACT_APP_SUPER_ADMIN &&
                !isDeleteDisabled && (
                  <span onClick={deletePopUp}>
                    <Deleteicon
                      value={props.value}
                      className="cursor-pointer mr-1"
                    />
                  </span>
                )}
 
              {isdelete && (
                <Confirmpopup
                  className="DeltePopup"
                  show={isdelete}
                  title={t("deletepopuptext")}
                  message={t("deletepopupmessage")}
                  handleToggle={deletePopUp}
                  row={props.row}
                  name={props.row.original.name}
                  deleteRow={deleteTableDataById}
                  cancel={t("cancelbutton")}
                  action={t("actionbutton")}
                />
              )}
            </div>
          );
        },
      },
    ],
    [t, roleName]
  );

  return (
    <div className="create-user facility-mgmt-table">
      <style>{`
        #light .facility-mgmt-table .iccc--table th {
          background: linear-gradient(120deg, #2E6DA4 0%, #3B82C4 100%) !important;
          color: #ffffff !important;
        }
        #light .facility-mgmt-table .iccc--table tbody tr td,
        #light .facility-mgmt-table .iccc--table tbody tr th {
          border: none !important;
        }
        #light .facility-mgmt-table .iccc--table tbody tr td {
          background-color: #fff !important;
        }
        #light .facility-mgmt-table .iccc--table tbody tr:nth-child(even) td {
          background-color: #F9FAFB !important;
        }
        .facility-mgmt-table .search-form {
          position: relative;
          display: inline-flex;
          align-items: center;
        }
        .facility-mgmt-table .search-form input[type="text"] {
          height: 36px !important;
          line-height: 34px !important;
          padding: 0 40px 0 12px !important;
          border: 1px solid #E4E7EC !important;
          border-radius: 8px !important;
          font-size: 12px !important;
          box-sizing: border-box !important;
        }
        .facility-mgmt-table .search-form button {
          position: absolute !important;
          top: 0 !important;
          bottom: 0 !important;
          right: 0 !important;
          height: 36px !important;
          width: 36px !important;
          margin: 0 !important;
          padding: 0 !important;
          background-color: transparent !important;
          border: none !important;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .facility-mgmt-table .search-icon {
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .facility-mgmt-table .search-icon svg {
          display: block;
          color: #2E6DA4;
        }
      `}</style>
      <Loader
        show={loader}
        type="body"
        stack="vertical"
        message="Loading Data"
      />
      <div style={{ background: "#fff", border: "1px solid #EEF1F5", borderRadius: "10px", boxShadow: "0 1px 2px rgba(16,24,40,0.04)", padding: "10px 14px", margin: "12px 12px 12px 12px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px" }}>
          <span style={{ width: "20px", height: "20px", borderRadius: "6px", background: "#EBF4FA", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <FilterIcon size={11} color="#2E6DA4" />
          </span>
          <span style={{ fontSize: "12px", fontWeight: 800, color: "#2E6DA4" }}>Filters</span>
        </div>
        <div style={{ display: "flex", gap: "14px", flexWrap: "wrap", alignItems: "flex-end" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px", flex: "0 1 200px" }}>
            <label style={filterLabelStyle}>Facility Type</label>
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} style={filterControlStyle}>
              <option value="">Facility Type</option>
              {typeOptions.map((o) => (<option key={o} value={o}>{o}</option>))}
            </select>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px", flex: "0 1 200px" }}>
            <label style={filterLabelStyle}>Contact Person</label>
            <select value={contactFilter} onChange={(e) => setContactFilter(e.target.value)} style={filterControlStyle}>
              <option value="">Contact Person</option>
              {contactOptions.map((o) => (<option key={o} value={o}>{o}</option>))}
            </select>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px", flex: "0 1 200px" }}>
            <label style={filterLabelStyle}>District</label>
            <select value={districtFilter} onChange={(e) => setDistrictFilter(e.target.value)} style={filterControlStyle}>
              <option value="">District</option>
              {districtOptions.map((o) => (<option key={o} value={o}>{o}</option>))}
            </select>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px", flex: "0 1 200px" }}>
            <label style={filterLabelStyle}>Block</label>
            <select value={blockFilter} onChange={(e) => setBlockFilter(e.target.value)} style={filterControlStyle}>
              <option value="">Block</option>
              {blockOptions.map((o) => (<option key={o} value={o}>{o}</option>))}
            </select>
          </div>
          {hasActiveFilters && (
            <button
              type="button"
              onClick={clearAllFilters}
              style={{ height: "34px", padding: "0 12px", borderRadius: "8px", border: "1px solid #E4E7EC", background: "#F9FAFB", color: "#475467", fontSize: "12px", fontWeight: 600, cursor: "pointer" }}
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>
      <div className="datatable__container">
        {facilityTableData && (
          <Table
            data={filteredData}
            title=""
            showFilter={false}
            columns={columns}
            numberOfRows={10}
            addButtonLabel={t("facilityaddButtonLabel")}
            addNewHandler={addNewHandler}
            setSearchData={setSearchData}
            searchPlaceholder="Search by Facility Name, Type, District, Block, Address"
            searchInputStyle={{ width: "19em" }}
            showExport={false}
            showExcelExport={true}
            exportData={filteredData}
            exportColumns={facilitycolumnsForExport}
            exportFileName="FacilityManagement"
            showReset={false}
            onExpand={false}
            showSearch={true}
            isHeaderFixed={true}
            rowCount={filteredData.length}
            enableColumnFilters={true}
            headerTableHeadStyle={{ background: "linear-gradient(120deg, #2E6DA4 0%, #3B82C4 100%)", color: "#FFFFFF" }}
            tableStyle={{
              border: "1px solid #EEF1F5",
              borderRadius: "10px",
              boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
            }}
          />
        )}
      </div>
    </div>
  );
};

export default FacilityList;
